import { HeaderIcons } from '../shared/icons';
import React, { useState, useEffect, useRef } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../shared/Header';
import { StandingsSidebar, LiveAnswer, LoadingQuestion, TimerSection, WaitingSpinner } from '../shared';
import { GameEndScreen } from './GameEndScreen';
import type { GameStatistics } from '../../types/game';
import { getGameName } from '../../utils/gameUtils';
import { useGameEndScreenData } from '../../hooks/useGameEndScreenData';
import '../../App.css';
import '../../styles/game-layout.css';
import { useTranslation } from '../../i18n';

interface SpeedChallengeQuestion {
  questionId?: string;
  id?: string;
  question: string;
  options: string[];
  timeLimit: number;
  questionNumber: number;
  totalQuestions: number;
  startTime: number;
  interesting?: string;
  challengeType: 'type-word' | 'emoji-identify' | 'math' | 'reverse' | 'color-sequence' | 'pattern' | 'shape';
  displayContent: string;
  displayColors?: string[];
  isSpeedChallenge?: boolean;
}

interface QuestionResult {
  questionId: string;
  correctAnswer: string;
  submissions: Array<{
    playerId: string;
    answer: string;
    responseTime: number;
    isCorrect: boolean;
    points: number;
  }>;
  leaderboard: Array<{
    playerId: string;
    playerName: string;
    score: number;
  }>;
  rankedPlayers?: Array<{
    playerId: string;
    playerName: string;
    rank: number;
  }>;
  isSpeedChallenge?: boolean;
}

interface SpeedChallengeHostDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{
      id: string;
      name: string;
      score: number;
      avatar?: string;
    }>;
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: SpeedChallengeQuestion;
    questionStartTime?: number;
  };
  socket: WebSocketAdapter;
  onBackToGameSelection?: () => void;
  myPlayerId?: string;
  hostIsPlaying?: boolean;
  onGoHome?: () => void;
  onLoadingTimeout?: () => void;
}

const SpeedChallengeHostDisplay: React.FC<SpeedChallengeHostDisplayProps> = ({ 
  roomState, 
  socket, 
  onBackToGameSelection,
  hostIsPlaying = false,
  onGoHome,
  onLoadingTimeout,
  myPlayerId,
}) => {
  const { t } = useTranslation();
  const [currentQuestion, setCurrentQuestion] = useState<SpeedChallengeQuestion | null>(
    roomState.currentQuestion || null
  );
  const [timeRemaining, setTimeRemaining] = useState<number>(
    roomState.currentQuestion?.timeLimit || 0
  );
  const [questionResult, setQuestionResult] = useState<QuestionResult | null>(null);
  const [showHostDidYouKnow, setShowHostDidYouKnow] = useState(false);
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<Array<{ playerId: string; playerName: string; score: number }>>([]);
  const [gameStatistics, setGameStatistics] = useState<GameStatistics | null>(null);
  const [waitingForNewGame, setWaitingForNewGame] = useState(false);
  const [liveAnswers, setLiveAnswers] = useState<LiveAnswer[]>([]);
  const [shouldUpdateStandings, setShouldUpdateStandings] = useState(false);
  
  // Host playing mode state
  const [answer, setAnswer] = useState('');
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [answerCorrect, setAnswerCorrect] = useState<boolean | null>(null);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [rankPhrase, setRankPhrase] = useState<{ emoji: string; text: string; color: string } | null>(null);
  const [correctAnswerDisplay, setCorrectAnswerDisplay] = useState<string | null>(null);
  const [acceptedAnswers, setAcceptedAnswers] = useState<string[] | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isSubmittingRef = useRef<boolean>(false);
  const didYouKnowTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  const previousQuestionIdRef = useRef<string | undefined>(undefined);
  const roomNameRef = useRef<string>(roomState.roomName);
  // Use ref for players to avoid socket listener cleanup/setup race condition
  const playersRef = useRef(roomState.players);

  const gameEndData = useGameEndScreenData(roomState);

  const cancelDidYouKnowReveal = () => {
    if (didYouKnowTimeoutRef.current != null) {
      clearTimeout(didYouKnowTimeoutRef.current);
      didYouKnowTimeoutRef.current = null;
    }
  };
  
  // Helper to reset host playing state
  const resetHostPlayingState = () => {
    setAnswer('');
    setAnswerSubmitted(false);
    setAnswerCorrect(null);
    setPointsEarned(0);
    setRankPhrase(null);
    setCorrectAnswerDisplay(null);
    setAcceptedAnswers(null);
    setLiveAnswers([]);
    isSubmittingRef.current = false;
    // Focus input for quick typing
    if (hostIsPlaying) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  useEffect(() => {
    roomNameRef.current = roomState.roomName;
  }, [roomState.roomName]);

  // Keep playersRef in sync with roomState.players
  useEffect(() => {
    playersRef.current = roomState.players;
  }, [roomState.players]);

  // When server says game is finished (e.g. reconnection after missing game-ended), show game end screen
  useEffect(() => {
    if (roomState.gameState === 'finished' && gameEndData) {
      setGameEnded(true);
      setCurrentQuestion(null);
      setFinalLeaderboard(gameEndData.finalLeaderboard);
      setGameStatistics(gameEndData.gameStatistics);
    }
  }, [roomState.gameState, gameEndData]);

  // Update local state when roomState.currentQuestion changes
  useEffect(() => {
    if (roomState.gameState === 'finished') return;
    if (roomState.currentQuestion) {
      const newQuestionId = roomState.currentQuestion.questionId || roomState.currentQuestion.id;
      const isNewQuestion = newQuestionId !== previousQuestionIdRef.current;
      
      // Use full timeLimit - user doesn't care about timer accuracy, just that game works
      const calculatedTimeRemaining = roomState.currentQuestion.timeLimit;
      
      // Update if it's a different question OR if we don't have a current question yet
      if (isNewQuestion || !currentQuestion) {
        if (isNewQuestion) {
          console.log(`SpeedChallengeHostDisplay: New question from room-state. ID: ${newQuestionId}`);
        } else {
          console.log(`SpeedChallengeHostDisplay: Refreshing question state (reconnection). ID: ${newQuestionId}, TimeRemaining: ${calculatedTimeRemaining}ms`);
        }
        previousQuestionIdRef.current = newQuestionId;
        setCurrentQuestion(roomState.currentQuestion);
        setTimeRemaining(calculatedTimeRemaining);
        setQuestionResult(null);
        setShowHostDidYouKnow(false);
        // Reset standings update flag for new question
        setShouldUpdateStandings(false);
        // Only reset host playing state if it's a new question, not if we're just syncing mid-game or reconnecting
        if (isNewQuestion) {
          resetHostPlayingState();
        }
      }
    } else if (roomState.gameState === 'waiting') {
      setCurrentQuestion(null);
      setQuestionResult(null);
      setShowHostDidYouKnow(false);
      setWaitingForNewGame(false);
      setGameEnded(false);
      previousQuestionIdRef.current = undefined;
      resetHostPlayingState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomState.currentQuestion, roomState.questionStartTime, roomState.gameState, hostIsPlaying, currentQuestion, timeRemaining]);

  useEffect(() => {
    const handleQuestion = (data: SpeedChallengeQuestion) => {
      console.log('SpeedChallengeHostDisplay received question:', data);
      const newQuestionId = data.questionId || data.id;
      cancelDidYouKnowReveal();
      setShowHostDidYouKnow(false);
      
      // Use full timeLimit on reconnection - user doesn't care about timer accuracy, just that game works
      // This simplifies the logic and ensures the game continues to flow
      const calculatedTimeRemaining = data.timeLimit;
      
      // ALWAYS update when question event is received (backend is sending a fresh question)
      // This ensures host receives new questions after reconnection
      // The question event is authoritative - it means backend is actively sending this question
      if (newQuestionId !== previousQuestionIdRef.current || !currentQuestion) {
        console.log(`SpeedChallengeHostDisplay: Updating from question event. ID: ${newQuestionId}, Previous: ${previousQuestionIdRef.current}`);
        previousQuestionIdRef.current = newQuestionId;
        setCurrentQuestion(data);
        setQuestionResult(null);
        setTimeRemaining(calculatedTimeRemaining);
        resetHostPlayingState();
      } else {
        // Even if same ID, update if we're receiving a question event (means backend is resending/refreshing)
        console.log(`SpeedChallengeHostDisplay: Refreshing question from event (same ID: ${newQuestionId})`);
        setCurrentQuestion(data);
        setTimeRemaining(calculatedTimeRemaining);
        setQuestionResult(null);
        resetHostPlayingState();
      }
    };

    const handleQuestionResult = (data: QuestionResult) => {
      console.log('Question result:', data);
      setQuestionResult(data);
      setShowHostDidYouKnow(false);
      cancelDidYouKnowReveal();
      // Trigger score update in StandingsSidebar
      setShouldUpdateStandings(true);
      // Reset after a short delay to allow the update to process
      setTimeout(() => {
        setShouldUpdateStandings(false);
      }, 100);
      // Trigger flip after delay — cancel on new question / unmount so it cannot flip the next card
      didYouKnowTimeoutRef.current = setTimeout(() => {
        didYouKnowTimeoutRef.current = null;
        setShowHostDidYouKnow(true);
      }, 1500);
    };

    const handleGameEnded = (data: {
      leaderboard: Array<{ playerId: string; playerName: string; score: number }>;
      statistics?: GameStatistics;
    }) => {
      cancelDidYouKnowReveal();
      setGameEnded(true);
      const sortedLeaderboard = [...(data.leaderboard || [])].sort((a, b) => b.score - a.score);
      setFinalLeaderboard(sortedLeaderboard);
      setGameStatistics(data.statistics || null);
      setCurrentQuestion(null);
      setQuestionResult(null);
    };

    const handleGameStarted = () => {
      console.log('SpeedChallengeHostDisplay: game-started');
      cancelDidYouKnowReveal();
      setShowHostDidYouKnow(false);
      setWaitingForNewGame(false);
      setGameEnded(false);
      setGameStatistics(null);
      setFinalLeaderboard([]);
      // Don't set currentQuestion to null here - let the question event set the actual question
      // This avoids a race condition where the UI gets stuck on "Loading question..."
      setQuestionResult(null);
      previousQuestionIdRef.current = undefined;
      resetHostPlayingState();
      socket.emit('get-room-state', { roomName: roomNameRef.current });
    };
    
    // Handle answer-received for host playing mode
    const handleAnswerReceived = (data: { 
      isCorrect: boolean; 
      points: number; 
      responseTime: number; 
      rank?: number | null;
      correctAnswer?: string;
      acceptedAnswers?: string[];
    }) => {
      console.log('SpeedChallengeHostDisplay answer-received:', data);
      isSubmittingRef.current = false;
      setAnswerSubmitted(true);
      setAnswerCorrect(data.isCorrect);
      setPointsEarned(data.points);
      if (data.rank) {
        // Generate rank phrase
        const pickRandom = (arr: string[]): string => arr[Math.floor(Math.random() * arr.length)];
        const rankColors: Record<number, string> = {
          1: '#FACC15', 2: '#CBD5E1', 3: '#F59E0B', 4: '#34D399', 5: '#34D399',
        };
        const rankEmojis: Record<number, string> = {
          1: '🥇', 2: '🥈', 3: '🥉', 4: '⚡', 5: '👍',
        };
        const rankKey = data.rank >= 1 && data.rank <= 5 ? String(data.rank) : 'default';
        const texts = t(`gameplay.speedChallenge.rankFeedback.${rankKey}`, { returnObjects: true }) as string[];
        const emoji = rankEmojis[data.rank] ?? '✓';
        const color = rankColors[data.rank] ?? '#34D399';
        setRankPhrase({ emoji, text: pickRandom(texts), color });
      }
      // Store correct answer info for wrong answers
      if (!data.isCorrect && data.correctAnswer) {
        setCorrectAnswerDisplay(data.correctAnswer);
        setAcceptedAnswers(data.acceptedAnswers || null);
      }
    };

    // Handle live answers from players
    const handlePlayerAnswered = (data: LiveAnswer) => {
      setLiveAnswers((prev) => {
        // Don't add duplicate answers from the same player
        if (prev.some((a) => a.playerId === data.playerId)) return prev;
        return [...prev, data];
      });
    };

    socket.on('question', handleQuestion);
    socket.on('question-result', handleQuestionResult);
    socket.on('game-ended', handleGameEnded);
    socket.on('game-started', handleGameStarted);
    socket.on('answer-received', handleAnswerReceived);
    socket.on('player-answered', handlePlayerAnswered);

    return () => {
      cancelDidYouKnowReveal();
      // Pass specific handler to only remove THIS component's listener, not App.tsx's
      socket.off('question', handleQuestion);
      socket.off('question-result', handleQuestionResult);
      socket.off('game-ended', handleGameEnded);
      socket.off('game-started', handleGameStarted);
      socket.off('answer-received', handleAnswerReceived);
      socket.off('player-answered', handlePlayerAnswered);
    };
    // Using playersRef instead of roomState.players to avoid socket listener race condition
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, hostIsPlaying]);

  // Track previous players for reference
  const previousPlayersRef = useRef<typeof roomState.players>([]);
  useEffect(() => {
    previousPlayersRef.current = roomState.players;
  }, [roomState.players]);

  // Timer countdown
  useEffect(() => {
    if (currentQuestion && timeRemaining > 0 && !questionResult) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 100) return 0;
          return prev - 100;
        });
      }, 100);

      return () => clearInterval(timer);
    }
  }, [currentQuestion, timeRemaining, questionResult]);

  // Game ended screen
  if (gameEnded) {
    const handlePlayAnotherGame = () => {
      if (onBackToGameSelection) {
        onBackToGameSelection();
      } else {
        window.location.reload();
      }
    };

    const handlePlaySameGame = () => {
      if (socket && roomState) {
        setGameEnded(false);
        setFinalLeaderboard([]);
        setCurrentQuestion(null);
        setQuestionResult(null);
        setWaitingForNewGame(true);
        socket.emit('start-game', { roomName: roomState.roomName });
      }
    };

    return (
      <GameEndScreen
        finalLeaderboard={finalLeaderboard.length > 0 ? finalLeaderboard : (gameEndData?.finalLeaderboard ?? [])}
        gameStatistics={gameStatistics ?? gameEndData?.gameStatistics ?? null}
        onPlaySameGame={handlePlaySameGame}
        onPlayAnotherGame={handlePlayAnotherGame}
        onBackToGameSelection={onBackToGameSelection}
        roomName={roomState.roomName}
        gameType={roomState.gameType}
      />
    );
  }

  // Loading states
  if (!currentQuestion && !waitingForNewGame && !gameEnded) {
    return (
      <LoadingQuestion
        socket={socket}
        roomName={roomState.roomName}
        title={t('gameplay.loading.question')}
        onGoHome={onGoHome}
        onLongWaitTimeout={onLoadingTimeout}
      />
    );
  }

  const gameDisplayName = getGameName(roomState.gameType);

  if (waitingForNewGame || !currentQuestion) {
    return (
      <div className="app">
        <div className="container">
          <h1 className="title">{t('gameplay.speedChallenge.starting')}</h1>
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <div style={{
              display: 'inline-block',
              width: '40px',
              height: '40px',
              border: '4px solid rgba(248, 250, 252, 0.3)',
              borderTop: '4px solid #F8FAFC',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}></div>
          </div>
        </div>
      </div>
    );
  }

  const timeSeconds = Math.ceil(timeRemaining / 1000);
  const progress = (timeRemaining / currentQuestion.timeLimit) * 100;

  const handleRestartGame = () => {
    if (socket && roomState) {
      setWaitingForNewGame(true);
      setGameEnded(false);
      setCurrentQuestion(null);
      resetHostPlayingState();
      socket.emit('start-game', { roomName: roomState.roomName });
    }
  };

  const handleBackToGameSelection = () => {
    if (onBackToGameSelection) {
      onBackToGameSelection();
    }
  };

  const handleSubmitAnswer = () => {
    if (!answer.trim() || !currentQuestion || answerSubmitted || isSubmittingRef.current) {
      return;
    }

    const questionId = currentQuestion.questionId || currentQuestion.id;
    if (!questionId) {
      console.error('Question ID not found');
      return;
    }

    isSubmittingRef.current = true;
    setAnswerSubmitted(true);

    socket.emit('submit-answer', {
      roomName: roomState.roomName,
      questionId: questionId,
      answer: answer.trim().toUpperCase(),
      timestamp: Date.now(),
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmitAnswer();
    }
  };

  const getChallengeHint = (type: string, question?: string): string => {
    if ((type === 'pattern' || type === 'shape') && question) {
      return question;
    }
    const hints: Record<string, string> = {
      'type-word': t('gameplay.speedChallenge.hints.typeWord'),
      'emoji-identify': t('gameplay.speedChallenge.hints.emojiIdentify'),
      'math': t('gameplay.speedChallenge.hints.math'),
      'reverse': t('gameplay.speedChallenge.hints.reverse'),
      'color-sequence': t('gameplay.speedChallenge.hints.colorSequence'),
      'pattern': t('gameplay.speedChallenge.hints.pattern'),
      'shape': t('gameplay.speedChallenge.hints.shape'),
    };
    return hints[type] || t('gameplay.speedChallenge.hints.default');
  };

  const getPromptLabel = (question: SpeedChallengeQuestion): string => {
    if (question.challengeType === 'type-word' || question.challengeType === 'reverse') {
      return question.displayContent.toUpperCase();
    }
    if (question.challengeType === 'math') {
      return `${question.displayContent} = ?`;
    }
    return question.displayContent.toUpperCase();
  };

  const getDisplayStyle = (type: string): React.CSSProperties => {
    switch (type) {
      case 'emoji-identify':
      case 'shape':
        return { fontSize: 'clamp(2.5rem, 6vw, 4rem)', lineHeight: '1.2' };
      case 'type-word':
      case 'reverse':
        return { fontSize: 'clamp(1.5rem, 3.5vw, 2.5rem)', fontFamily: 'monospace', letterSpacing: '0.15em' };
      case 'math':
      case 'pattern':
        return { fontSize: 'clamp(1.3rem, 3vw, 2rem)', fontFamily: 'monospace' };
      case 'color-sequence':
        return { fontSize: 'clamp(2rem, 5vw, 3.5rem)', letterSpacing: '0.3em' };
      default:
        return { fontSize: 'clamp(1.3rem, 3vw, 2rem)' };
    }
  };

  return (
    <div className="app">
      <Header
        leftButton={{
          icon: HeaderIcons.back,
          onClick: handleBackToGameSelection,
          tooltip: t('gameplay.backToGameSelection'),
        }}
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: handleRestartGame,
          tooltip: t('gameplay.restartGame'),
        }}
        centerContent={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
            <div className="header-game-name" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className="game-header-label">{t('gameplay.partyHost.gameLabel')}</span>
              <span className="game-header-value">{gameDisplayName}</span>
            </div>
            <div className="game-header-divider header-game-name" />
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className="game-header-label">{t('gameplay.partyHost.roomCodeLabel')}</span>
              <div style={{
                fontSize: '1.3rem',
                fontWeight: 'bold',
                letterSpacing: '0.15rem',
                color: 'var(--color-primary)',
                padding: '0.3rem 0.8rem',
                backgroundColor: 'rgba(var(--color-primary-rgb), 0.1)',
                borderRadius: '6px',
                border: '2px solid var(--color-primary)',
              }}>
                {roomState.roomName}
              </div>
            </div>
          </div>
        }
      />

      <div className="game-host-layout">
        <div className={`game-host-left no-image ${!hostIsPlaying ? 'host-only' : ''}`}>
          <div className="game-host-timer-section">
            <TimerSection
              questionNumber={currentQuestion.questionNumber}
              totalQuestions={currentQuestion.totalQuestions}
              timeSeconds={timeSeconds}
              progress={progress}
            />
          </div>

          <div className="game-host-question-section">
            <div className="game-display-centered-content" style={{ width: '100%' }}>
              <h1 className="game-question-text" style={{
                color: 'var(--color-primary)',
                marginBottom: '1rem',
                letterSpacing: '0.03em',
                textShadow: '0 2px 8px rgba(var(--color-primary-rgb), 0.3)',
              }}>
                {currentQuestion.question}
              </h1>

              <div
                className="question-display__speed-content"
                style={{
                  ...getDisplayStyle(currentQuestion.challengeType),
                  margin: '0 auto 1.5rem',
                  padding: '1rem 1.5rem',
                  borderRadius: '15px',
                  width: '100%',
                  maxWidth: '700px',
                  minHeight: '80px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  fontWeight: 800,
                  color: '#F8FAFC',
                  backgroundColor: 'rgba(var(--color-primary-rgb), 0.15)',
                  border: '2px solid rgba(var(--color-primary-rgb), 0.45)',
                }}
              >
                {getPromptLabel(currentQuestion)}
              </div>

              {questionResult && (
                <div className="wcib-result-card-wrap" style={{ marginTop: '2rem', width: '100%' }}>
                  <div
                    className="wcib-result-card"
                    style={{
                      display: 'grid',
                      gridTemplateRows: '1fr',
                      gridTemplateColumns: '1fr',
                      transformStyle: 'preserve-3d',
                      transition: 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                      transform: showHostDidYouKnow && currentQuestion.interesting ? 'rotateY(180deg)' : 'rotateY(0deg)',
                    }}
                  >
                    <div style={{
                      gridRow: 1,
                      gridColumn: 1,
                      padding: '1.5rem 2rem',
                      backgroundColor: 'rgba(var(--color-primary-rgb), 0.2)',
                      border: '3px solid var(--color-primary)',
                      borderRadius: '15px',
                      textAlign: 'center',
                      backfaceVisibility: 'hidden',
                      alignSelf: 'start',
                    }}>
                      <p style={{ fontSize: '1.2rem', color: 'rgba(255,255,255,0.7)', margin: 0 }}>
                        {t('gameplay.speedChallenge.correctAnswerColon')}
                      </p>
                      <p style={{ fontSize: '2rem', color: 'var(--color-primary)', fontWeight: 'bold', margin: '0.5rem 0 0 0' }}>
                        {questionResult.correctAnswer}
                      </p>
                    </div>
                    {currentQuestion.interesting && (
                      <div
                        className="wcib-did-you-know"
                        style={{
                          gridRow: 1,
                          gridColumn: 1,
                          padding: '1.5rem 2rem',
                          backgroundColor: 'rgba(var(--color-primary-rgb), 0.2)',
                          border: '3px solid var(--color-primary)',
                          borderRadius: '15px',
                          textAlign: 'center',
                          backfaceVisibility: 'hidden',
                          transform: 'rotateY(180deg)',
                          alignSelf: 'start',
                        }}
                      >
                        <p className="wcib-did-you-know-title" style={{ color: 'var(--color-primary)', fontWeight: 'bold', margin: '0 0 0.8rem 0' }}>
                          💡 Did you know?
                        </p>
                        <p className="wcib-did-you-know-body" style={{ color: '#fff', lineHeight: '1.6', margin: 0 }}>
                          {currentQuestion.interesting}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {hostIsPlaying && !answerSubmitted && (
                <div style={{ marginTop: '2rem', width: '100%', maxWidth: '600px', margin: '2rem auto 0' }}>
                  <style>{`
                    .speed-input-host::placeholder {
                      font-size: 0.9rem;
                      letter-spacing: 0;
                      text-transform: none;
                    }
                  `}</style>
                  <input
                    ref={inputRef}
                    type="text"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value.toUpperCase())}
                    onKeyDown={handleKeyDown}
                    placeholder={getChallengeHint(currentQuestion.challengeType, currentQuestion.question)}
                    autoComplete="off"
                    autoCapitalize="characters"
                    className="speed-input-host"
                    style={{
                      width: '100%',
                      padding: '0.85rem',
                      fontSize: 'clamp(0.85rem, 2.1vw, 1.25rem)',
                      fontFamily: 'monospace',
                      textAlign: 'center',
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      backgroundColor: 'rgba(255, 255, 255, 0.92)',
                      border: '3px solid rgba(var(--color-primary-rgb), 0.4)',
                      borderRadius: '15px',
                      color: 'var(--text-primary)',
                      caretColor: 'var(--color-primary)',
                      outline: 'none',
                    }}
                  />
                  <button
                    onClick={handleSubmitAnswer}
                    disabled={!answer.trim()}
                    className="button"
                    style={{
                      width: '100%',
                      marginTop: '1rem',
                      backgroundColor: answer.trim() ? 'var(--color-primary)' : 'rgba(248, 250, 252, 0.3)',
                      opacity: answer.trim() ? 1 : 0.5,
                      cursor: answer.trim() ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {t('gameplay.submitAnswer')}
                  </button>
                </div>
              )}

              {hostIsPlaying && answerSubmitted && (
                <div style={{ marginTop: '2rem', width: '100%', maxWidth: '600px', margin: '2rem auto 0' }}>
                  <div style={{
                    padding: '1.5rem',
                    backgroundColor: answerCorrect ? 'rgba(var(--color-primary-rgb), 0.2)' : 'rgba(239, 68, 68, 0.2)',
                    border: `2px solid ${answerCorrect ? 'var(--color-primary)' : '#EF4444'}`,
                    borderRadius: '15px',
                    textAlign: 'center',
                    color: '#fff',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                        {answerCorrect ? '✓' : '✗'}
                      </span>
                      <span style={{ fontSize: '1.3rem', fontWeight: 'bold' }}>
                        {answerCorrect
                          ? (rankPhrase ? `${rankPhrase.text} (+${pointsEarned})` : t('gameplay.speedChallenge.correctShort'))
                          : t('gameplay.feedback.wrongAnswer')}
                      </span>
                    </div>

                    {!answerCorrect && correctAnswerDisplay && (
                      <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(239, 68, 68, 0.3)' }}>
                        <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)', margin: 0 }}>
                          {t('gameplay.speedChallenge.correctAnswerLower')}
                        </p>
                        <p style={{ fontSize: '1.2rem', color: '#fff', fontWeight: 'bold', margin: '0.3rem 0' }}>
                          {correctAnswerDisplay}
                        </p>
                        {acceptedAnswers && acceptedAnswers.length > 1 && (
                          <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', margin: '0.5rem 0 0 0' }}>
                            Also accepted: {acceptedAnswers.filter((a) => a !== correctAnswerDisplay).join(', ')}
                          </p>
                        )}
                      </div>
                    )}

                    <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)', margin: '1rem 0 0 0' }}>
                      Your answer: <span style={{ fontWeight: 'bold' }}>{answer}</span>
                    </p>
                  </div>
                  <WaitingSpinner isLoading={false} />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="game-host-right">
          <StandingsSidebar
            players={roomState.players}
            liveAnswers={liveAnswers}
            totalPlayers={roomState.players.length}
            questionId={currentQuestion?.questionId || currentQuestion?.id}
            shouldUpdateScores={shouldUpdateStandings}
            theme={{
              primary: 'var(--color-primary)',
              primaryLight: 'rgba(var(--color-primary-rgb), 0.15)',
              accent: 'var(--color-tertiary)',
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default SpeedChallengeHostDisplay;

